import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { whenNoDialog } from "@/lib/modalGate";
import { Illustration } from "@/components/Illustration";

interface AnnouncementFeature {
  emoji: string;
  title: string;
  description: string;
}

interface Announcement {
  id: string;
  title: string;
  subtitle: string;
  features: AnnouncementFeature[];
  isActive: boolean;
  createdAt: string;
}

const SEEN_KEY = "seenAnnouncements";

function getSeenIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
  } catch {
    return [];
  }
}

function markSeen(id: string) {
  try {
    const seen = getSeenIds();
    if (!seen.includes(id)) {
      localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, id]));
    }
  } catch {}
}

export function AnnouncementModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const { data: announcement } = useQuery<Announcement | null>({
    enabled: !!user,
    queryKey: ["/api/announcements/active"],
    queryFn: async () => {
      const res = await fetch("/api/announcements/active");
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 1000 * 60 * 10, // 10 min
  });

  useEffect(() => {
    if (!announcement || dismissed) return;
    const seen = getSeenIds();
    if (seen.includes(announcement.id)) return;
    // Let the grade check (or anything else already open) finish first.
    const cancel = whenNoDialog(() => setOpen(true), { everyMs: 3000, maxWaitMs: 120000 });
    return cancel;
  }, [announcement]);

  function handleDismiss() {
    if (announcement) markSeen(announcement.id);
    setOpen(false);
    setDismissed(true);
  }

  if (!announcement) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-md w-full p-0 overflow-hidden gap-0 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-primary/5 border-b px-6 pt-6 pb-5 text-center flex-shrink-0">
          <Illustration name="owl-megaphone" size={120} className="mx-auto" />
          <h2 className="mt-3 text-xl font-bold leading-tight">{announcement.title}</h2>
          {announcement.subtitle && (
            <p className="text-sm text-muted-foreground mt-1">{announcement.subtitle}</p>
          )}
        </div>

        {/* Feature bullets */}
        {announcement.features && announcement.features.length > 0 && (
          <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
            {announcement.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0 leading-tight">{f.emoji}</span>
                <div>
                  <p className="font-semibold text-sm">{f.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pb-5 pt-1 flex-shrink-0 border-t">
          <Button onClick={handleDismiss} className="w-full rounded-lg">
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
